import React, { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import PurchaseTable from '../components/purchases/PurchaseTable'
import SearchFilters from '../components/forms/SearchFilters'
import Pagination from '../components/common/Pagination'
import PurchaseModal from '../components/modals/PurchaseModal'
import PurchaseDetailModal from '../components/modals/PurchaseDetailModal'
// import { filterByDateRange } from '../utils/calculations'
import './Purchases.scss'

const Purchases = ({
  shopInfo,
  products,
  purchases,
  onPurchaseSave,
  loading,
  nextClientId,
}) => {
  const [purchaseSearch, setPurchaseSearch] = useState('')
  const [purchaseFilter, setPurchaseFilter] = useState('all')
  const [currentPurchasePage, setCurrentPurchasePage] = useState(1)
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [editingPurchase, setEditingPurchase] = useState(null)
  const [viewingPurchase, setViewingPurchase] = useState(null)

  // États pour les filtres
  const [purchaseDateFilter, setPurchaseDateFilter] = useState('all')
  const [purchaseStartDate, setPurchaseStartDate] = useState('')
  const [purchaseEndDate, setPurchaseEndDate] = useState('')
  const [remainingPaymentFilter, setRemainingPaymentFilter] = useState('all')
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('all')

  const itemsPerPage = 5

  // Filtrage des achats
  const allFilteredPurchases = purchases.filter((purchase) => {
    const matchesSearch =
      purchase.clientName
        .toLowerCase()
        .includes(purchaseSearch.toLowerCase()) ||
      (purchase.clientPhone && purchase.clientPhone.includes(purchaseSearch))

    if (!matchesSearch) return false

    // Filtre par méthode de paiement
    if (purchaseFilter !== 'all') {
      const hasPaymentMethod = purchase.paymentMethods
        ? purchase.paymentMethods.some((pm) => pm.method === purchaseFilter)
        : purchase.paymentMethod === purchaseFilter
      if (!hasPaymentMethod) return false
    }

    // Filtre par reste à payer
    if (
      remainingPaymentFilter === 'remaining' &&
      (!purchase.remainingAmount || purchase.remainingAmount <= 0)
    ) {
      return false
    } else if (
      remainingPaymentFilter === 'paid' &&
      purchase.remainingAmount > 0
    ) {
      return false
    }

    // Filtre par statut de livraison
    if (deliveryStatusFilter !== 'all') {
      const status = purchase.deliveryStatus || 'default'
      if (status !== deliveryStatusFilter) return false
    }

    // Filtre par date
    if (
      purchaseDateFilter === 'custom' &&
      (purchaseStartDate || purchaseEndDate)
    ) {
      const purchaseDate = new Date(purchase.date)
      const start = purchaseStartDate
        ? new Date(purchaseStartDate)
        : new Date('1970-01-01')
      const end = purchaseEndDate
        ? new Date(purchaseEndDate + 'T23:59:59')
        : new Date()

      if (purchaseDate < start || purchaseDate > end) {
        return false
      }
    }

    return true
  })

  // Pagination
  const totalPurchases = allFilteredPurchases.length
  const startPurchaseIndex = (currentPurchasePage - 1) * itemsPerPage
  const endPurchaseIndex = startPurchaseIndex + itemsPerPage
  const filteredPurchases = allFilteredPurchases.slice(
    startPurchaseIndex,
    endPurchaseIndex,
  )

  // Reset page si pas d'éléments sur la page courante
  useEffect(() => {
    if (
      totalPurchases > 0 &&
      filteredPurchases.length === 0 &&
      currentPurchasePage > 1
    ) {
      setCurrentPurchasePage(1)
    }
  }, [totalPurchases, filteredPurchases.length, currentPurchasePage])

  const handlePurchasePageChange = (newPage) => {
    setCurrentPurchasePage(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleEditPurchase = (purchase) => {
    setEditingPurchase(purchase)
    setShowPurchaseModal(true)
  }

  const handlePurchaseSave = (purchaseData, existingPurchase) => {
    onPurchaseSave(purchaseData, existingPurchase)
    setShowPurchaseModal(false)
    setEditingPurchase(null)
  }

  const searchFilters = [
    {
      value: purchaseFilter,
      onChange: setPurchaseFilter,
      options: [
        { value: 'all', label: 'Tous les paiements' },
        { value: 'especes', label: 'Espèces' },
        { value: 'orange-money', label: 'Orange Money' },
        { value: 'mtn-money', label: 'MTN Money' },
        { value: 'autre', label: 'Autre' },
      ],
    },
  ]

  const extraFilters = (
    <div className='search-row filter-purchase'>
      <div className='form-field filter-actif'>
        <label>Période</label>
        <select
          value={purchaseDateFilter}
          onChange={(e) => setPurchaseDateFilter(e.target.value)}
        >
          <option value='all'>Toutes les dates</option>
          <option value='custom'>Période personnalisée</option>
        </select>
        {purchaseDateFilter !== 'all' && (
          <p className='filter-actif-repert'></p>
        )}
      </div>
      {purchaseDateFilter === 'custom' && (
        <>
          <div className='form-field'>
            <label>Du</label>
            <input
              type='date'
              value={purchaseStartDate}
              onChange={(e) => setPurchaseStartDate(e.target.value)}
            />
          </div>
          <div className='form-field'>
            <label>Au</label>
            <input
              type='date'
              value={purchaseEndDate}
              onChange={(e) => setPurchaseEndDate(e.target.value)}
            />
          </div>
        </>
      )}
      <div className='form-field filter-actif'>
        <label>Reste à payer</label>
        <select
          value={remainingPaymentFilter}
          onChange={(e) => setRemainingPaymentFilter(e.target.value)}
        >
          <option value='all'>Tous</option>
          <option value='remaining'>Avec reste à payer</option>
          <option value='paid'>Entièrement payé</option>
        </select>

        {remainingPaymentFilter !== 'all' && (
          <p className='filter-actif-repert'></p>
        )}
      </div>

      <div className='form-field filter-actif'>
        <label>Statut de livraison</label>
        <select
          value={deliveryStatusFilter}
          onChange={(e) => setDeliveryStatusFilter(e.target.value)}
        >
          <option value='all'>Tous les statuts</option>
          <option value='pending'>À livrer</option>
          <option value='delivered'>Livré</option>
          <option value='default'>Non définie</option>
        </select>

        {deliveryStatusFilter !== 'all' && (
          <p className='filter-actif-repert'></p>
        )}
      </div>
    </div>
  )

  return (
    <div className='content-space'>
      <div className='section-header'>
        <h2 className='section-title'>Achats</h2>
        <button
          onClick={() => {
            setEditingPurchase(null)
            setShowPurchaseModal(true)
          }}
          className='btn initial'
        >
          <Plus size={15} className='btn-icon' />
          Nouvel achat
        </button>
      </div>

      <SearchFilters
        searchValue={purchaseSearch}
        onSearchChange={setPurchaseSearch}
        filters={searchFilters}
        extraFilters={extraFilters}
        extraFiltersDisable={
          purchaseDateFilter !== 'all' ||
          remainingPaymentFilter !== 'all' ||
          deliveryStatusFilter !== 'all'
        }
      />

      <div className='table-container'>
        {totalPurchases === 0 ? (
          <div className='empty-state'>
            {purchaseSearch ||
            purchaseFilter !== 'all' ||
            remainingPaymentFilter !== 'all' ||
            purchaseDateFilter !== 'all'
              ? 'Aucun achat trouvé avec ces critères'
              : 'Aucun achat enregistré'}
          </div>
        ) : (
          <>
            <PurchaseTable
              purchases={filteredPurchases}
              onView={setViewingPurchase}
              onEdit={handleEditPurchase}
            />

            <Pagination
              currentPage={currentPurchasePage}
              totalItems={totalPurchases}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePurchasePageChange}
            />
          </>
        )}
      </div>

      {/* Modales */}
      <PurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => {
          setShowPurchaseModal(false)
          setEditingPurchase(null)
        }}
        purchase={editingPurchase}
        onSave={handlePurchaseSave}
        products={products}
        purchases={purchases}
        loading={loading.saving}
        nextClientId={nextClientId}
      />

      <PurchaseDetailModal
        shopInfo={shopInfo}
        purchase={viewingPurchase}
        isOpen={!!viewingPurchase}
        onClose={() => setViewingPurchase(null)}
        onEdit={handleEditPurchase}
      />
    </div>
  )
}

export default Purchases
